/**
 * Chat App Preview Card
 *
 * Native chat handoff for building a Losi App preview from a marker.
 *
 * Primary responsibilities:
 * - Build a validated app draft from a chat prompt marker
 * - Open the reusable preview modal and create flow
 *
 * Dependencies:
 * - Apps API client
 * - App preview modal
 */

'use client'

import { useContext, useMemo, useState, type ReactNode } from 'react'
import { useRouter } from 'nextjs-toploader/app'
import { ArrowRight, LayoutPanelTop, ShieldCheck } from 'lucide-react'
import { LosiAssistantDefault, LosiMain } from 'marshmallow-icons'

import { AppPreviewModal } from '@/components/apps/app-preview-modal'
import {
  createApp,
  createAppDraft,
  type AppDraftResponse
} from '@/components/apps/apps-api-client'
import { Button } from '@/components/ui/button'
import { ChatbotUIContext } from '@/context/context'
import { useBetaFeatureEnabled } from '@/hooks/use-beta-feature-access'
import { LOSI_APPS_BETA_FEATURE_KEY } from '@/lib/beta-features/registry'
import type { AppDefinition } from '@/lib/apps/definition'
import type { LosiAppPreviewMarkerPayload } from '@/lib/apps/preview-marker'
import { cn } from '@/lib/utils'
import type { AppPreviewDraftState } from '@/types/losi-apps'

interface ChatAppPreviewCardProps {
  className?: string
  payload: LosiAppPreviewMarkerPayload
}

const TEMPLATE_LABELS: Record<string, string> = {
  ai_readiness_audit: 'AI readiness audit',
  bug_triage: 'Bug triage',
  client_onboarding: 'Client onboarding',
  content_campaign: 'Content campaign',
  meeting_to_execution: 'Meeting to execution',
  proposal_builder: 'Proposal builder',
  research_tracker: 'Research tracker',
  sales_follow_up: 'Sales follow-up',
  social_repurposing: 'Social repurposing',
  website_improvement: 'Website improvement'
}

// --------------

export function ChatAppPreviewCard({ className, payload }: ChatAppPreviewCardProps) {
  const router = useRouter()
  const { selectedWorkspace } = useContext(ChatbotUIContext)
  const { enabled: appsBetaEnabled } = useBetaFeatureEnabled(LOSI_APPS_BETA_FEATURE_KEY)
  const workspaceId = selectedWorkspace?.id ?? null
  const [draft, setDraft] = useState<AppDraftResponse | null>(null)
  const [draftSessionId, setDraftSessionId] = useState(0)
  const [previewState, setPreviewState] = useState<AppPreviewDraftState | null>(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [isBuilding, setIsBuilding] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const templateLabel = useMemo(
    () => (payload.templateId ? TEMPLATE_LABELS[payload.templateId] : 'Auto-picked template'),
    [payload.templateId]
  )

  const title = payload.title ?? 'Losi App draft'
  const description =
    payload.description ??
    'This opens the generated app draft so you can test it, change it, and create the same app from the preview.'

  if (!appsBetaEnabled) {
    return null
  }

  const openPreview = async () => {
    if (draft) {
      setIsPreviewOpen(true)
      return
    }

    if (!workspaceId) {
      setError('Choose a workspace before previewing this app.')
      return
    }

    setIsBuilding(true)
    setError(null)

    try {
      const nextDraft = await createAppDraft({
        prompt: payload.prompt,
        templateId: payload.templateId,
        workspaceId
      })
      setDraft(nextDraft)
      setDraftSessionId((current) => current + 1)
      setPreviewState(null)
      setIsPreviewOpen(true)
    } catch (buildError) {
      setError(
        buildError instanceof Error ? buildError.message : 'Losi could not build the preview.'
      )
    } finally {
      setIsBuilding(false)
    }
  }

  const updateDraftDefinition = (definition: AppDefinition) => {
    setDraft((current) => (current ? { ...current, definition } : current))
  }

  const createDraftApp = async () => {
    if (!workspaceId || !draft) return

    setIsCreating(true)
    setError(null)

    try {
      const response = await createApp({
        definition: draft.definition,
        previewState,
        workspaceId
      })
      router.push(`/apps/${response.app.id}`)
    } catch (createError) {
      setError(
        createError instanceof Error
          ? createError.message
          : 'Something went wrong while saving this app.'
      )
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <div
      className={cn(
        'not-prose bg-background my-3 overflow-hidden rounded-xl border shadow-sm',
        className
      )}
    >
      <div className="grid min-w-0 gap-0 lg:grid-cols-[minmax(0,1fr)_220px]">
        <div className="min-w-0 p-4">
          <div className="flex items-start gap-3">
            <div className="bg-muted/70 flex size-11 shrink-0 items-center justify-center rounded-lg border">
              <LosiMain aria-hidden="true" className="size-6" color="currentColor" />
            </div>
            <div className="min-w-0">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <p className="min-w-0 break-words text-sm font-semibold">{title}</p>
                <span className="text-muted-foreground rounded-full border px-2 py-0.5 text-[11px] font-medium">
                  {templateLabel}
                </span>
              </div>
              <p className="text-muted-foreground mt-1 max-w-2xl text-sm leading-6">
                {description}
              </p>
            </div>
          </div>

          <div className="mt-4 grid min-w-0 gap-2 md:grid-cols-3">
            <PreviewPoint icon={<LayoutPanelTop className="size-4" />} label="Working preview" />
            <PreviewPoint
              icon={
                <LosiAssistantDefault
                  aria-hidden="true"
                  className="size-4"
                  color="currentColor"
                />
              }
              label="AI actions"
            />
            <PreviewPoint icon={<ShieldCheck className="size-4" />} label="Safe create flow" />
          </div>

          {error ? (
            <p className="border-destructive/30 bg-destructive/5 text-destructive mt-3 rounded-md border px-3 py-2 text-sm">
              {error}
            </p>
          ) : null}
        </div>

        <div className="bg-muted/30 flex min-w-0 flex-col justify-between gap-3 border-t p-4 lg:border-l lg:border-t-0">
          <div>
            <p className="text-muted-foreground text-xs font-semibold uppercase tracking-[0.14em]">
              Losi Apps
            </p>
            <p className="text-muted-foreground mt-2 text-sm leading-6">
              Open the made app preview, then create it from the modal.
            </p>
          </div>
          <Button className="w-full justify-between" loading={isBuilding} onClick={openPreview}>
            {draft ? 'Open app preview' : 'Build app preview'}
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </div>

      {draft ? (
        <AppPreviewModal
          key={draftSessionId}
          backLabel="Back to chat"
          definition={draft.definition}
          explanation={draft.explanation}
          isCreating={isCreating}
          onCreate={createDraftApp}
          onDefinitionChange={updateDraftDefinition}
          onOpenChange={setIsPreviewOpen}
          onPreviewStateChange={setPreviewState}
          open={isPreviewOpen}
          workspaceId={workspaceId}
        />
      ) : null}
    </div>
  )
}

// --------------

function PreviewPoint({ icon, label }: { icon: ReactNode; label: string }) {
  return (
    <div className="bg-card text-muted-foreground flex min-w-0 items-center gap-2 rounded-lg border px-3 py-2 text-xs">
      <span className="text-foreground">{icon}</span>
      <span className="min-w-0 break-words">{label}</span>
    </div>
  )
}
